import type { ConfigProps, TypedWrappedFieldProps } from "redux-form";
import { Field, isPristine } from "redux-form";

import { Button, Grid, RoundedBox } from "@braintrust/braintrust-ui-components";
import { createFormInstance } from "@js/forms/components";
import { useAppSelector } from "@js/hooks";
import type { InvoiceAttachment } from "@js/types/invoices";

import { AttachmentsField, NotesToRecipientField } from "../../fields";

import { InvoiceErrors } from "./components/invoice-errors";
import { Header, InvoiceItemsTable, InvoiceTotal } from "./components";
import type { InvoiceFormData } from "./types";
import { INVOICE_FORM_ID } from "./types";

import styles from "./style.module.scss";

export type AddInvoiceFormComponentProps = Omit<
  ConfigProps<InvoiceFormData>,
  "form"
> & {
  enableReinitialize: boolean;
  isCopy: boolean;
  isEdit?: boolean;
  canChangeJob: boolean;
  existingAttachments?: InvoiceAttachment[];
  isExpenseOnlyInvoiceForm: boolean;
  isInvoiceEditingDisabled?: boolean;
};

const AddInvoiceFormInstance = createFormInstance<InvoiceFormData, unknown>(
  INVOICE_FORM_ID,
);

export const AddInvoiceForm = ({
  existingAttachments,
  isEdit,
  isCopy,
  canChangeJob,
  isExpenseOnlyInvoiceForm,
  isInvoiceEditingDisabled,
  ...props
}: AddInvoiceFormComponentProps) => {
  return (
    <AddInvoiceFormInstance className={styles.invoiceForm} {...props}>
      <RoundedBox square className={styles.invoiceFormWrapper}>
        <Header
          isCopy={isCopy}
          canChangeJob={canChangeJob}
          isEdit={isEdit}
          isExpenseOnlyInvoiceForm={isExpenseOnlyInvoiceForm}
        />
        <div className={styles.invoiceFormContent}>
          <InvoiceItemsTable
            isExpenseOnlyInvoiceForm={isExpenseOnlyInvoiceForm}
            isInvoiceEditingDisabled={!!isInvoiceEditingDisabled}
          />
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <NotesToRecipientField />
              <AttachmentsField existingAttachments={existingAttachments} />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
              className={styles.invoiceFormTotal}
            >
              <Field name="new_items" component={InvoiceTotal} />
            </Grid>
          </Grid>
        </div>
      </RoundedBox>
      <div className={styles.invoiceFormActions}>
        <InvoiceErrors />
        <Field name="" component={SendButton} isEdit={!!isEdit} />
      </div>
    </AddInvoiceFormInstance>
  );
};

type SendButtonProps = TypedWrappedFieldProps & {
  isEdit: boolean;
};

const SendButton = ({ meta, isEdit }: SendButtonProps) => {
  const pristine = useAppSelector((state) => isPristine(meta.form)(state));

  return (
    <Button
      variant="primary"
      type="submit"
      disabled={isEdit ? pristine : false}
    >
      Send
    </Button>
  );
};
